import React, { useContext, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { AppContext, EMPTY_STRING, ModalContext } from 'commons-ui';

import useService from '../useService';
import { ApproveContext } from '../approveContext';
import { KYC_STATUS_REJECTED } from '../constants';

interface IApproveFormProps {
	uid: number;
	kycName: string;
	bankName: string;
	onCancel: () => void;
	onConfirm: () => Promise<void>;
}

/** Shows both names side by side, because that comparison is the decision being made. */
const ApproveForm: React.FC<IApproveFormProps> = ({ uid, kycName, bankName, onCancel, onConfirm }) => {
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [css] = useStyletron();

	return (
		<div className={css({ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' })}>
			<div>
				Approving this KYC for <strong>{uid}</strong> will leave the user&apos;s bank account linked and restore withdrawals.
				Confirm the names belong to the same person.
			</div>
			<div className={css({ display: 'flex', gap: '40px' })}>
				<div>
					<div className={css({ fontWeight: 'bold' })}>KYC Name</div>
					<div>{kycName || '-'}</div>
				</div>
				<div>
					<div className={css({ fontWeight: 'bold' })}>Bank Account Name</div>
					<div>{bankName || '-'}</div>
				</div>
			</div>
			<div className={css({ display: 'flex', gap: '12px', justifyContent: 'flex-end' })}>
				<Button kind={KIND.tertiary} size={SIZE.compact} onClick={onCancel} disabled={submitting}>
					Cancel
				</Button>
				<Button
					kind={KIND.primary}
					size={SIZE.compact}
					disabled={submitting}
					isLoading={submitting}
					onClick={async () => {
						// Guarded as well as disabled: a double-click must not fire two approvals.
						if (submitting) return;
						setSubmitting(true);
						await onConfirm();
						setSubmitting(false);
					}}>
					Approve KYC
				</Button>
			</div>
		</div>
	);
};

interface IApproveCellProps {
	kycId: number;
	uid: number;
	status: string;
	kycName: string;
	bankName: string;
}

/**
 * Per-row "Approve".
 *
 * Only a REJECTED row can be approved; anything else (already approved) shows no button. The button
 * is also hidden when the signed-in user lacks the override permission — a courtesy, not the
 * enforcement, which admin.service does on the call.
 *
 * A component rather than plain JSX inside `renderCell` because it needs hooks — baseui invokes
 * renderCell as a function, so hooks used directly there would not be attached to a component.
 */
const ApproveCell: React.FC<IApproveCellProps> = ({ kycId, uid, status, kycName, bankName }) => {
	const { setModalConfig } = useContext(ModalContext);
	const { showAlert } = useContext(AppContext);
	const { refresh, canApprove } = useContext(ApproveContext);
	const { overrideBankNameMismatchKyc } = useService();
	const [css] = useStyletron();

	if (status !== KYC_STATUS_REJECTED || !canApprove) {
		return <span>{EMPTY_STRING}</span>;
	}

	const openConfirmation = () => {
		setModalConfig({
			isOpen: true,
			header: 'Approve bank name mismatch',
			content: (
				<ApproveForm
					uid={uid}
					kycName={kycName}
					bankName={bankName}
					onCancel={() => setModalConfig({ isOpen: false })}
					onConfirm={async () => {
						const { success, error } = await overrideBankNameMismatchKyc(kycId);
						setModalConfig({ isOpen: false });
						if (success) {
							showAlert('KYC approved', { type: 'success' });
							refresh();
						} else {
							showAlert(error || 'Could not approve this KYC', { type: 'error' });
						}
					}}
				/>
			)
		});
	};

	return (
		<div className={css({ display: 'flex' })}>
			<Button kind={KIND.secondary} size={SIZE.compact} onClick={openConfirmation}>
				Approve
			</Button>
		</div>
	);
};

export default ApproveCell;
